/* eslint-disable import/namespace */
import * as Sentry from '@sentry/react'
import {
	addUiBreadcrumb,
	isSentryEnabled,
	serializeData,
	setSentryUserContext,
} from './SentryMonitoring'

// Generiere oder hole eine Login-Session-ID
// Diese wird verwendet, um alle Login-Events zum selben Issue zu gruppieren
const getLoginSessionId = () => {
	const sessionKey = 'sentry_login_session_id'
	let sessionId = sessionStorage.getItem(sessionKey)

	if (!sessionId) {
		sessionId = `login_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
		sessionStorage.setItem(sessionKey, sessionId)
	}

	return sessionId
}

const logRedirect = (component, data) => {
	// Serialisiere alle Daten, um [object Object] zu vermeiden
	const serializedData = serializeData(data)

	const logData = {
		timestamp: new Date().toISOString(),
		component,
		...serializedData,
		action: serializedData?.action || 'Redirect event',
		currentUrl: serializedData?.currentUrl || window.location.href,
		currentPath: serializedData?.currentPath || window.location.pathname,
	}

	// Console logging (für Browser-Konsole)
	// eslint-disable-next-line no-console
	console.log('[REDIRECT LOG]', component + ':', logData)

	// Sentry logging (für Produktions-Monitoring)
	try {
		addUiBreadcrumb({
			category: 'redirect',
			message: `${component}: ${logData.action}`,
			level: 'info',
			data: logData,
		})
	} catch (error) {
		// Fallback falls Sentry nicht initialisiert ist
		// eslint-disable-next-line no-console
		console.warn('[REDIRECT LOG] Sentry not available:', error)
	}
}

// Sendet ein Event mit allen bisherigen Breadcrumbs
// Verwendet die gleiche Session-ID, damit alle Events zum selben Issue gehören
const sendBreadcrumbsNow = (component, data = {}) => {
	if (!isSentryEnabled) {
		return
	}

	// Serialisiere alle Daten, um [object Object] zu vermeiden
	const serializedData = serializeData(data)

	const sessionId = getLoginSessionId()
	const logData = {
		timestamp: new Date().toISOString(),
		component,
		...serializedData,
		action: serializedData?.action || 'Important step in login flow',
		currentUrl: serializedData?.currentUrl || window.location.href,
		currentPath: serializedData?.currentPath || window.location.pathname,
		sessionId,
	}

	// Console logging
	// eslint-disable-next-line no-console
	console.log('[REDIRECT LOG] Important step:', component + ':', logData)

	// Breadcrumb hinzufügen
	try {
		addUiBreadcrumb({
			category: 'login_flow',
			message: `${component}: ${logData.action}`,
			level: 'info',
			data: logData,
		})

		// Event senden mit Session-ID als Fingerprint
		// Alle Events mit der gleichen Session-ID werden zum selben Issue gruppiert
		Sentry.captureMessage(`Login flow: ${logData.action}`, {
			level: 'info',
			tags: {
				event: 'login_flow',
				component,
				sessionId,
			},
			extra: logData,
			fingerprint: [sessionId],
		})
	} catch (error) {
		// Fallback falls Sentry nicht initialisiert ist
		// eslint-disable-next-line no-console
		console.warn('[REDIRECT LOG] Sentry not available:', error)
	}
}

// Sendet das finale Event beim erfolgreichen Login
// Verwendet die gleiche Session-ID wie die vorherigen Events, damit alles zum selben Issue gehört
const logLoginComplete = (profile, auth) => {
	if (!isSentryEnabled) {
		return
	}

	if (!auth?.access_token || !profile) {
		// eslint-disable-next-line no-console
		console.warn('[LOGIN LOG] Missing auth or profile data')
		return
	}

	// Prüfen, ob bereits ein Login-Event für diesen Token gesendet wurde
	const loginTokenKey = 'sentry_login_token'
	const lastLoggedToken = sessionStorage.getItem(loginTokenKey)

	// Wenn der Token gleich ist, wurde bereits ein Event gesendet
	if (lastLoggedToken === auth.access_token) {
		return
	}

	const sessionId = getLoginSessionId()
	const loginData = {
		timestamp: new Date().toISOString(),
		username: profile?.username,
		userId: profile?.id,
		tenantIds: auth?.tenant_id || [],
		roles: profile?.roles || [],
		loginMethod: 'SSO',
		currentUrl: window.location.href,
		currentPath: window.location.pathname,
		sessionId,
	}

	// Console logging
	// eslint-disable-next-line no-console
	console.log('[LOGIN LOG] Sending final login event to Sentry:', loginData)

	// Finales Event senden mit der gleichen Session-ID
	// Dadurch werden alle Events vom Login-Flow zum selben Issue gruppiert
	try {
		setSentryUserContext(profile, auth)

		Sentry.captureMessage('User Login Flow - Completed', {
			level: 'info',
			tags: {
				event: 'login',
				username: profile?.username || 'unknown',
				sessionId,
			},
			extra: loginData,
			// Gleicher Fingerprint wie die anderen Events = selbes Issue
			fingerprint: [sessionId],
		})

		// Token speichern, damit für diesen Token kein weiteres Event gesendet wird
		sessionStorage.setItem(loginTokenKey, auth.access_token)
		// Session-ID löschen, damit beim nächsten Login eine neue erstellt wird
		sessionStorage.removeItem('sentry_login_session_id')
	} catch (error) {
		// Fallback falls Sentry nicht initialisiert ist
		// eslint-disable-next-line no-console
		console.warn('[LOGIN LOG] Sentry not available:', error)
	}
}

export default logRedirect
export { logLoginComplete, sendBreadcrumbsNow }
